import React, { useState, useEffect } from "react";
import RegistrationForm from "../components/RegistrationForm";
import WhatsAppBot from "../components/WhatsAppBot";
import ParticleBackground from "../components/ParticleBackground";
import logo from "../assets/ai_gurukul_logo.png";
import udaanLogo from "../assets/AI_Udaan_Logo.png";
import pragatiLogo from "../assets/AI_Pragati_Logo.png";
import shilpLogo from "../assets/AI_Shilp_Logo.png";
import heroVisual from "../assets/hero_visual.png";
import heroVisual2 from "../assets/hero_visual_2.png";
import heroVisual3 from "../assets/hero_visual_3.png";
import heroVisual4 from "../assets/hero_visual_4.png";
import heroVisual5 from "../assets/hero_visual_5.png";

import bg from "../assets/bg.png"; // Imported background image
import "../styles/LandingPage.css";

const sections = {
  hero: "hero",
  problem: "problem",
  solution: "solution",
  learn: "learn",
  syllabus: "syllabus",
  demo: "demo",
  transformation: "transformation",
  parents: "parents",
  stats: "stats",
  testimonials: "testimonials",
  howItWorks: "how-it-works",
  urgency: "urgency",
  register: "register"
};

const statsData = [
  { number: "100%", label: "Of careers will be impacted by AI" },
  { number: "Early", label: "Advantage for students learning AI early" },
  { number: "#1", label: "New digital literacy necessity" }
];

export default function Home() {
  const [activeNavItem, setActiveNavItem] = useState('Home');
  const heroImages = [heroVisual, heroVisual2, heroVisual3, heroVisual4, heroVisual5];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isHovered, heroImages.length]);

  // Scroll-based active navigation highlighting
  useEffect(() => {
    const navItemsMap = {
      [sections.hero]: 'Home',
      [sections.solution]: 'About',
      [sections.howItWorks]: 'Work',
      [sections.syllabus]: 'Programs',
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const activeName = navItemsMap[entry.target.id];
            if (activeName) {
              setActiveNavItem(activeName);
            }
          }
        });
      },
      {
        root: null,
        // Adjust these margins to trigger the highlight when the section is near the middle of the screen
        rootMargin: "-20% 0px -40% 0px",
        threshold: 0
      }
    );

    // Delay slightly to ensure DOM is fully painted before observing
    const timer = setTimeout(() => {
      Object.keys(navItemsMap).forEach((id) => {
        const sectionEl = document.getElementById(id);
        if (sectionEl) {
          observer.observe(sectionEl);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  const [showExitPopup, setShowExitPopup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProgram, setActiveProgram] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState("919225584952");
  const [isBotOpen, setIsBotOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    // Dynamic WhatsApp Expert Routing logic from the bot
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) {
      setWhatsappNumber("919225584952"); // Morning Slot
    } else if (hour >= 12 && hour < 18) {
      setWhatsappNumber("919822217064"); // Afternoon Slot
    } else {
      setWhatsappNumber("919822219735"); // Evening Slot
    }
  }, []);

  const openModal = (program) => {
    setActiveProgram(program);
    setIsModalOpen(true);
    setExpandedModule(null); // Reset accordion on open
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveProgram(null);
    document.body.style.overflow = 'auto';
  };

  // Close on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Fade-in on scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.fade-in-section').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Use exit popup logic
  useEffect(() => {
    const handleMouseOut = (e) => {
      if (e.clientY <= 0 && !localStorage.getItem('exitPopupShown')) {
        setShowExitPopup(true);
        localStorage.setItem('exitPopupShown', 'true');
      }
    };
    document.addEventListener('mouseleave', handleMouseOut);
    return () => document.removeEventListener('mouseleave', handleMouseOut);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 90; // Adjust for sticky navbar 
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="landing-page-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', marginLeft: '10px' }}>
      <div style={{ flex: 1 }}>
        <ParticleBackground />
        {/* PREMIUM STICKY NAVBAR */}
        <nav className="navbar">
          <div className="nav-brand" onClick={() => scrollToSection(sections.hero)}>
            <img src={logo} alt="AI Gurukul" className="nav-logo-img" />
            <span className="gradient-text-accent nav-brand-text">AI GURUKUL</span>
          </div>

          {/* Desktop Links */}
          <div className={`nav-links ${isMenuOpen ? 'mobile-open' : ''}`}>
            {['Home', 'About', 'Work', 'Programs'].map((item) => (
              <a
                key={item}
                className={`nav-link-hover ${activeNavItem === item ? 'active' : ''}`}
                style={activeNavItem === item ? { color: 'var(--accent-cyan)', textShadow: '0 0 10px rgba(0, 229, 255, 0.5)' } : { cursor: 'pointer' }}
                onClick={() => {
                  setActiveNavItem(item);
                  const target = {
                    'Home': sections.hero,
                    'About': sections.solution,
                    'Work': sections.howItWorks,
                    'Programs': sections.syllabus
                  }[item];
                  scrollToSection(target);
                  setIsMenuOpen(false);
                }}
              >
                {item}
              </a>
            ))}
            <button className="btn-primary" onClick={() => { scrollToSection(sections.register); setIsMenuOpen(false); }}>
              Enroll Now
            </button>
          </div>

          <button className="mobile-menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? '✕' : '☰'}
          </button>
        </nav>

        {/* Sticky Elements */}
        <div className="sticky-cta">
          <button
            onClick={() => setIsBotOpen(!isBotOpen)}
            className="whatsapp-float"
            style={{ border: 'none', cursor: 'pointer', padding: 0 }}
            aria-label="Toggle WhatsApp Bot"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
          </button>
          <button className="sticky-cta-btn" onClick={() => scrollToSection(sections.register)} style={{ whiteSpace: 'nowrap', minWidth: '160px', alignItems: 'center', justifyContent: 'center' }}>
            Enroll Now
          </button>
        </div>

        {/* ANNOUNCEMENT STRIP */}
        <div className="announcement-strip">
          <div className="announcement-marquee">
            <span>
              No Fees. Just Future Skills. &nbsp;&nbsp;•&nbsp;&nbsp;
              Talent Gets Rewarded — Learn AI for Free &nbsp;&nbsp;•&nbsp;&nbsp;
              Your Future Shouldn’t Depend on Fees &nbsp;&nbsp;•&nbsp;&nbsp;
              We Invest in Talent — Not Fees &nbsp;&nbsp;•&nbsp;&nbsp;
              Because Talent Deserves Opportunity &nbsp;&nbsp;•&nbsp;&nbsp;
              Give Your Child a Future — Not a Fee Burden &nbsp;&nbsp;•&nbsp;&nbsp;
              Opportunity Should Be Earned, Not Bought &nbsp;&nbsp;•&nbsp;&nbsp;
              Empowering Bright Minds — Free of Cost &nbsp;&nbsp;•&nbsp;&nbsp;
              No Financial Barriers. Only Learning. &nbsp;&nbsp;•&nbsp;&nbsp;
              Earn Your Spot. Learn for Free. &nbsp;&nbsp;•&nbsp;&nbsp;
              Don’t Miss Your Chance to Learn AI for Free &nbsp;&nbsp;•&nbsp;&nbsp;
              Selected Minds. Unlimited Future. Zero Cost. &nbsp;&nbsp;•&nbsp;&nbsp;
            </span>
            <span>
              No Fees. Just Future Skills. &nbsp;&nbsp;•&nbsp;&nbsp;
              Talent Gets Rewarded — Learn AI for Free &nbsp;&nbsp;•&nbsp;&nbsp;
              Your Future Shouldn’t Depend on Fees &nbsp;&nbsp;•&nbsp;&nbsp;
              We Invest in Talent — Not Fees &nbsp;&nbsp;•&nbsp;&nbsp;
              Because Talent Deserves Opportunity &nbsp;&nbsp;•&nbsp;&nbsp;
              Give Your Child a Future — Not a Fee Burden &nbsp;&nbsp;•&nbsp;&nbsp;
              Opportunity Should Be Earned, Not Bought &nbsp;&nbsp;•&nbsp;&nbsp;
              Empowering Bright Minds — Free of Cost &nbsp;&nbsp;•&nbsp;&nbsp;
              No Financial Barriers. Only Learning. &nbsp;&nbsp;•&nbsp;&nbsp;
              Earn Your Spot. Learn for Free. &nbsp;&nbsp;•&nbsp;&nbsp;
              Don’t Miss Your Chance to Learn AI for Free &nbsp;&nbsp;•&nbsp;&nbsp;
              Selected Minds. Unlimited Future. Zero Cost. &nbsp;&nbsp;•&nbsp;&nbsp;
            </span>
          </div>
        </div>

        {/* SECTION 1: HERO */}
        <section id={sections.hero} className="hero-section">
          <div className="hero-glow-lights">
            <div className="blur-light bl-1"></div>
            <div className="blur-light bl-2"></div>
          </div>

          <div className="section-container">
            <div className="hero-container">
              <div className="hero-content">
                <div className="hero-content-inner">
                  {/* MAIN LOGO (TOP) */}
                  <div className="brand-header hero-brand">
                    <img src={logo} alt="AI Gurukul" className="main-logo-responsive" style={{
                      height: 'clamp(120px, 20vw, 250px)',
                      width: 'clamp(120px, 20vw, 250px)',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }} />
                  </div>



                  <h1 className="hero-headline">
                    <span style={{ whiteSpace: 'nowrap' }}>Build <span className="gradient-text-accent">Future-Ready</span></span> Minds with AI Education
                  </h1>
                  <p className="hero-subheadline">
                    Ancient Wisdom. Future Intelligence.
                    Empowering students with AI, creativity & real-world skills.
                  </p>

                  {/* Button Row */}
                  <div className="hero-social-proof">
                    <div className="hero-buttons">
                      <button className="btn-primary hero-enroll-btn" onClick={() => scrollToSection(sections.register)}>
                        🔵 Enroll Now
                      </button>
                    </div>
                    <p className="hero-trust-text">
                      ⭐ Trusted by 1000+ Students & Parents
                    </p>
                  </div>
                </div>
              </div>

              <div className="hero-visual"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onClick={nextImage}
              >
                <div style={{ position: 'relative', width: '100%', maxWidth: '550px', cursor: 'pointer' }}>
                  {heroImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Student learning AI ${idx + 1}`}
                      className="main-visual-img"
                      style={{
                        position: idx === 0 ? 'relative' : 'absolute',
                        top: 0,
                        left: 0,
                        opacity: currentImageIndex === idx ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out',
                        pointerEvents: currentImageIndex === idx ? 'auto' : 'none',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ))}
                </div>
                {/* Floating visual elements can be added with CSS overlays */}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEM */}
        <section id={sections.problem} className="problem-section fade-in-section">
          <div className="section-container">
            <h2 className="section-title problem-title">The Education System is Broken for the Future</h2>
            <div className="card-grid-4">
              <div className="problem-card">
                <span className="problem-icon">❌</span>
                <p className="problem-text" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Marks <span style={{ fontSize: '1.5em', color: 'var(--accent-cyan)', fontWeight: '900', margin: '0 8px', textShadow: '0 0 10px rgba(0, 229, 255, 0.5)', lineHeight: 1 }}>&gt;</span> Skills</p>
              </div>
              <div className="problem-card">
                <span className="problem-icon">❌</span>
                <p className="problem-text">Passive Learning</p>
              </div>
              <div className="problem-card">
                <span className="problem-icon">❌</span>
                <p className="problem-text">No AI Exposure</p>
              </div>
              <div className="problem-card">
                <span className="problem-icon">❌</span>
                <p className="problem-text">Wasted Screen Time</p>
              </div>
            </div>
            <p style={{ textAlign: 'center', marginTop: '25px', fontSize: '1.25rem', color: 'var(--accent-cyan)', fontWeight: 'bold' }}>
              Your child deserves better.
            </p>
          </div>
        </section>

        {/* SECTION 3: SOLUTION */}
        <section id={sections.solution} className="solution-section fade-in-section">
          <div className="section-container" style={{ textAlign: 'center' }}>
            <h2 className="section-title">Meet Us At AI Gurukul</h2>
            <p className="highlight-tagline">" Where ancient learning meets modern AI skills "</p>
            <div className="card-grid-3">
              <div className="glass-card" style={{ padding: '25px' }}>
                <div className="pillar-icon" style={{ fontSize: '3rem', marginBottom: '15px' }}>🧠</div>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '10px' }}>Think</h3>
                <p style={{ color: 'var(--accent-cyan)', fontWeight: '600' }}>Critical Thinking</p>
              </div>
              <div className="glass-card" style={{ padding: '25px' }}>
                <div className="pillar-icon" style={{ fontSize: '3rem', marginBottom: '15px' }}>⚙️</div>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '10px' }}>Build</h3>
                <p style={{ color: 'var(--accent-cyan)', fontWeight: '600' }}>Hands-on AI Projects</p>
              </div>
              <div className="glass-card" style={{ padding: '25px' }}>
                <div className="pillar-icon" style={{ fontSize: '3rem', marginBottom: '15px' }}>🚀</div>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '10px' }}>Lead</h3>
                <p style={{ color: 'var(--accent-cyan)', fontWeight: '600' }}>Future Confidence</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: WHAT STUDENTS LEARN */}
        <section id={sections.learn} className="learn-section fade-in-section">
          <div className="section-container">
            <h2 className="section-title">What Students Learn</h2>
            <div className="card-grid-2">
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>🤖</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>AI Basics</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>💬</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Prompt Engineering</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>🛠</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>AI Tools</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>📱</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Build Projects</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>🎨</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Creativity with AI</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '25px' }}>
                <span style={{ fontSize: '2.5rem' }}>🧩</span>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Problem Solving</h3>
              </div>
            </div>
            <div className="glass-card" style={{ marginTop: '25px', background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15), rgba(124, 58, 237, 0.15))', padding: '15px', textAlign: 'center', border: '1px solid rgba(0, 229, 255, 0.3)' }}>
              <p style={{ color: 'var(--accent-cyan)', fontWeight: '800', fontSize: '1.1rem', margin: 0 }}>No coding required for beginners</p>
            </div>
          </div>
        </section>



        {/* SECTION 6: TRANSFORMATION */}
        <section id={sections.transformation} className="transformation-section fade-in-section" style={{ marginTop: '80px' }}>
          <div className="section-container">
            <h2 className="section-title" style={{ marginBottom: '60px' }}>Transformation</h2>
            <div className="transformation-grid">
              <div className="glass-card transform-card before">
                <h3 className="transform-title-before">BEFORE</h3>
                <ul className="transform-list">
                  <li><span>❌</span> Consumes content</li>
                  <li><span>❌</span> Low confidence</li>
                  <li><span>❌</span> No practical skills</li>
                </ul>
              </div>

              <div className="transform-arrow">➜</div>

              <div className="glass-card transform-card after">
                <h3 className="transform-title-after">AFTER</h3>
                <ul className="transform-list">
                  <li><span style={{ color: '#10B981' }}>✅</span> Builds AI tools</li>
                  <li><span style={{ color: '#10B981' }}>✅</span> Thinks creatively</li>
                  <li><span style={{ color: '#10B981' }}>✅</span> Confident & future-ready</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: FOR PARENTS */}
        <section id={sections.parents} className="parents-section fade-in-section" style={{ marginTop: '80px' }}>
          <div className="section-container">
            <h2 className="section-title">Why Parents Choose AI Gurukul</h2>
            <div className="card-grid-2" style={{ maxWidth: '900px', margin: '0 auto' }}>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Future-ready skills</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Safe learning environment</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Productive screen time</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Builds confidence</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Beyond school curriculum</h3>
              </div>
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px' }}>
                <span style={{ fontSize: '2rem' }}>✔</span>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Structured and Engaging Programs</h3>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: WHY AI MATTERS */}
        <section id={sections.stats} className="stats-section fade-in-section" style={{ paddingBottom: '30px' }}>
          <div className="section-container">
            <h2 className="section-title">Why AI Matters</h2>
            <div className="card-grid-3">
              <div className="glass-card" style={{ textAlign: 'center', padding: 'var(--grid-gap)' }}>
                <span className="stats-number">100%</span>
                <p className="stats-text">“AI will impact 100% of careers”</p>
              </div>
              <div className="glass-card" style={{ textAlign: 'center', padding: 'var(--grid-gap)' }}>
                <span className="stats-number">10X</span>
                <p className="stats-text">“Students with AI skills earn more opportunities”</p>
              </div>
              <div className="glass-card" style={{ textAlign: 'center', padding: 'var(--grid-gap)' }}>
                <span className="stats-number">#1</span>
                <p className="stats-text">“Early learners stay ahead”</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 10: HOW IT WORKS (MOVED UP) */}
        <section id={sections.howItWorks} className="how-it-works-section" style={{ padding: '30px 0' }}>
          <div className="section-container">
            <h2 className="section-title">How It Works</h2>
            <div className="process-flow">
              {/* Step 1 */}
              <div className="glass-card process-card">
                <div className="process-icon">📅</div>
                <h3 className="process-step-title">Enroll Now</h3>
              </div>

              <div className="process-arrow">→</div>

              {/* Step 2 */}
              <div className="glass-card process-card">
                <div className="process-icon">👨‍🏫</div>
                <h3 className="process-step-title">Attend Class</h3>
              </div>

              <div className="process-arrow">→</div>

              {/* Step 3 */}
              <div className="glass-card process-card">
                <div className="process-icon">📝</div>
                <h3 className="process-step-title">Practice and Assessment</h3>
              </div>

              <div className="process-arrow">→</div>

              {/* Step 4 */}
              <div className="glass-card process-card">
                <div className="process-icon">🚀</div>
                <h3 className="process-step-title">Start Building</h3>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: TESTIMONIALS (MOVED DOWN) */}
        <section id={sections.testimonials} className="testimonials-section fade-in-section">
          <div className="section-container">
            <h2 className="section-title">What They Say</h2>
            <div className="card-grid-2" style={{ gap: '30px' }}>
              <div className="glass-card" style={{ position: 'relative', padding: '40px' }}>
                <span style={{ fontSize: '4rem', position: 'absolute', top: '10px', left: '20px', opacity: 0.1 }}>"</span>
                <p style={{ fontSize: '1.2rem', fontStyle: 'italic', position: 'relative', zIndex: 1 }}>“Best investment for my child. My child is now creating things instead of just consuming content. Best decision we made.”</p>
                <h4 style={{ marginTop: '20px', color: 'var(--accent-cyan)' }}>— Parent Testimonial</h4>
              </div>
              <div className="glass-card" style={{ position: 'relative', padding: '40px' }}>
                <span style={{ fontSize: '4rem', position: 'absolute', top: '10px', left: '20px', opacity: 0.1 }}>"</span>
                <p style={{ fontSize: '1.2rem', fontStyle: 'italic', position: 'relative', zIndex: 1 }}>“I built my first AI project in just a few classes! I am now building, not just watching.”</p>
                <h4 style={{ marginTop: '20px', color: 'var(--accent-cyan)' }}>— Student Testimonial</h4>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION: PROGRAMS & SYLLABUS */}
        <section id={sections.syllabus} className="programs-detail-section fade-in-section" style={{ padding: '60px 0', marginTop: '80px', background: 'rgba(5, 14, 33, 0.4)' }}>
          <div className="section-container">
            <h2 className="section-title" style={{ marginBottom: '40px' }}>Our Programs</h2>
            <div className="card-grid-3">

              <div className="kpi-card">
                <div className="kpi-logo-container">
                  <img src={udaanLogo} alt="AI Udaan Logo" className="kpi-logo" />
                </div>
                <h3 className="gradient-text-accent kpi-title">AI Udaan</h3>
                <p className="kpi-desc">Beginner-friendly AI program for school students (Std. 7-12)</p>

                <ul className="kpi-list">
                  <li>🔹 70% Hands-on learning</li>
                  <li>🔹 Build chatbots & tools</li>
                  <li>🔹 No coding required</li>
                </ul>

                <div className="view-details-btn-wrapper">
                  <button
                    className="view-details-btn"
                    onClick={() => openModal({
                      id: 'udaan',
                      icon: udaanLogo,
                      title: 'AI Udaan',
                      subtitle: 'School Intelligence Program (SIP)',
                      desc: 'A future ready AI skilling program designed for students from Std. 7 to 12. Mentored by Dr. Amit Andre.',
                      modules: [
                        {
                          title: 'Module 1: AI Foundations & Future Thinking (4 Hours)',
                          details: ['What is AI, ML, GenAI (real-life examples)', 'AI in daily life (YouTube, Instagram, Google Maps)', 'Careers in AI (simplified for school level)', 'Responsible AI & ethics'],
                          outcome: 'Students understand AI relevance and possibilities'
                        },
                        {
                          title: 'Module 2: Logic Building & Problem Solving (6 Hours)',
                          details: ['Algorithmic thinking (no coding approach)', 'Pattern recognition games', 'Decision-making frameworks', 'Introduction to flowcharts'],
                          outcome: 'Hands-on: Solve real-world problems using logic models'
                        },
                        {
                          title: 'Module 3: No-Code AI Tools & Prompt Engineering (8 Hours)',
                          details: ['ChatGPT / GenAI tools', 'AI image generators', 'AI presentation tools', 'Prompt engineering basics'],
                          outcome: 'Hands-on: Create AI-generated stories, images, presentations & basic chatbot'
                        },
                        {
                          title: 'Module 4: Data & AI Basics (6 Hours)',
                          details: ['What is data and why it matters', 'Types of data (text, images, numbers)', 'Basic data analysis (Excel/Google Sheets)', 'Introduction to datasets'],
                          outcome: 'Hands-on: Analyze student datasets and create insights'
                        },
                        {
                          title: 'Module 5: Build Your First AI Projects (10 Hours)',
                          details: ['AI Chatbot for School Helpdesk', 'AI Poster Generator for Events', 'AI Study Assistant', 'AI Quiz Generator', 'Smart Attendance Idea'],
                          outcome: 'Hands-on: Build + Present + Demonstrate'
                        },
                        {
                          title: 'Module 6: Innovation & Presentation (6 Hours)',
                          details: ['Design thinking basics', 'How to present ideas', 'Pitching skills (Shark Tank style)', 'Final demo day'],
                          outcome: 'Confidence + communication + innovation mindset'
                        }
                      ],
                      usps: [
                        { title: ' 1. Zero Cost – High Value Model', details: ['No financial barrier for students', 'Sponsored / CSR / institutional model', '👉 Outcome: Massive scale impact'] },
                        { title: ' 2. Hands-on First, Theory Later', details: ['70% practical, 30% concept', 'Students build AI, not just learn'] },
                        { title: ' 3. Industry-Designed Curriculum', details: ['Built by The DataTech Labs (AI Company)', 'Not academic-only → real-world use cases'] },
                        { title: ' 4. Age-Appropriate Learning Path', details: ['Simplified AI for 7th–12th', 'Layered complexity (junior vs senior students)'] },
                        { title: ' 5. Early Career Exposure', details: ['AI careers understanding', 'Future job landscape', 'Entrepreneurial mindset'] },
                        { title: ' 6. School Transformation', details: ['Converts schools into AI-enabled campuses', 'Enables: AI Clubs, Innovation Labs, Annual AI Exhibitions'] },
                        { title: ' 7. Certification + Recognition', details: ['Industry-recognized certificate', 'Future Internship pathways', 'Advanced AI tracks'] }
                      ],
                      whyUs: [
                        { title: ' 1. Industry-First AI Company (Not Coaching)', details: ['Real enterprise AI implementations', 'Exposure to actual use cases across industries'] },
                        { title: ' 2. Agentic AI & GenAI Expertise', details: ['Advanced capabilities beyond basic AI training', 'Students get exposure to future AI trends early'] },
                        { title: ' 3. Proven Ecosystem', details: ['Work with: Governments, Banks, Enterprises, Universities', '👉 Students learn what industry actually uses'] },
                        { title: ' 4. Scalable National Vision', details: ['Aligns with mission: “Making India the AI Capital of the World”'] },
                        { title: ' 5. End-to-End Support', details: ['Curriculum, Trainers, Platform, Certification, Showcase events'] }
                      ],
                      approach: [
                        '✅ Build minimum 2 AI-based mini projects',
                        '✅ Use real AI tools (not simulations)',
                        '✅ Work in teams (collaboration mindset)',
                        '✅ Present solutions in a Demo Day / AI Exhibition',
                        '✅ Receive certification from The DataTech Labs'
                      ],
                      outcomes: [
                        '✔ Understand AI practically',
                        '✔ Build and showcase AI projects',
                        '✔ Think logically and creatively',
                        '✔ Gain confidence in technology',
                        '✔ Be ready for advanced AI learning'
                      ],
                      positioning: '“AI Udaan by The DataTech Labs is not just a training program — it is a national movement to democratize AI learning for every student, regardless of background, cost, or geography.”'
                    })}
                  >
                    View Details
                  </button>
                </div>
              </div>

              <div className="kpi-card">
                <div className="kpi-logo-container">
                  <img src={pragatiLogo} alt="AI Pragati Logo" className="kpi-logo" />
                </div>
                <h3 className="gradient-text-accent kpi-title">AI Pragati</h3>
                <p className="kpi-desc">Designed for BBA, BCom, BA, MBA & non-tech UG/PG students</p>

                <ul className="kpi-list">
                  <li>🔹 AI for business productivity</li>
                  <li>🔹 No coding business apps</li>
                  <li>🔹 Placement & Job readiness</li>
                </ul>

                <div className="view-details-btn-wrapper">
                  <button
                    className="view-details-btn"
                    onClick={() => openModal({
                      id: 'pragati',
                      icon: pragatiLogo,
                      title: 'AI Pragati',
                      subtitle: 'Business & Career Intelligence Program',
                      desc: 'Designed for BBA, BCom, BA, MBA & non-tech UG/PG students. Mentored by Dr. Amit Andre.',
                      modules: [
                        {
                          title: 'Module 1: AI for Business Leaders (4 Hours)',
                          details: ['AI, GenAI, Agentic AI (non-technical understanding)', 'AI impact: Banking, Marketing, HR, Retail', 'How AI is changing jobs & careers'],
                          outcome: 'Outcome: Strategic awareness (not technical overload)'
                        },
                        {
                          title: 'Module 2: AI Productivity Stack (8 Hours)',
                          details: ['Emails, reports, presentations with AI', 'Resume building & job preparation', 'Research & summarization tools', 'AI presentation generators'],
                          outcome: 'Hands-on: Create full business reports and professional PPTs'
                        },
                        {
                          title: 'Module 3: AI for Business Functions (12 Hours)',
                          details: [
                            'Marketing: AI for social media, targeting, content',
                            'Finance: Budgeting, forecasting, fraud detection',
                            'HR: Recruitment, screening, employee analytics',
                            'Operations: Demand forecasting, inventory optimization'
                          ],
                          outcome: 'Hands-on: Create marketing campaigns & hiring workflows'
                        },
                        {
                          title: 'Module 4: Data Thinking for Managers (6 Hours)',
                          details: ['Understanding data (no coding)', 'Excel + AI integration', 'Dashboard basics (Power BI intro)'],
                          outcome: 'Hands-on: Build simple dashboards and interpret insights'
                        },
                        {
                          title: 'Module 5: AI Tools for Entrepreneurship (6 Hours)',
                          details: ['Idea validation using AI', 'Business plan generation', 'Market research using AI'],
                          outcome: 'Hands-on: Create startup idea & generate pitch deck'
                        },
                        {
                          title: 'Module 6: Capstone Project & AI Pitch Day (4 Hours)',
                          details: ['Domain-based projects (Marketing, HR, Finance)', 'Case studies & live applications'],
                          outcome: 'Outcome: Final presentation + industry evaluation'
                        }
                      ],
                      usps: [
                        { title: ' 1. No Coding Required – Business Focused', details: ['Designed for non-technical students', 'Focus on decision-making, not programming'] },
                        { title: ' 2. Industry-Ready Skillset in 40 Hours', details: ['Resume-ready projects', 'Practical exposure to AI tools'] },
                        { title: ' 3. Role-Based Learning', details: ['Marketing/Finance/HR student → Specialized AI track', '👉 Personalized relevance'] },
                        { title: ' 4. Immediate Career Impact', details: ['Automate daily tasks', 'Improve productivity by 3–5x', 'Stand out in placements'] },
                        { title: ' 5. Hands-on Business Simulation', details: ['Real-world case studies', 'Practical execution'] },
                        { title: ' 6. Startup & Entrepreneurial Mindset', details: ['AI-enabled business creation', 'Idea to pitch in 40 hours'] },
                        { title: ' 7. Certification with Industry Credibility', details: ['Certification by The DataTech Labs', 'Adds strong value to CV'] }
                      ],
                      whyUs: [
                        { title: ' 1. Real Enterprise Experience', details: ['BFSI, Government, Manufacturing, Retail focus', '👉 Students learn real business applications'] },
                        { title: ' 2. Agentic AI Expertise', details: ['Exposure to next-gen AI (beyond ChatGPT)', 'Understanding future trends'] },
                        { title: ' 3. CXO-Level Thinking Approach', details: ['Designed from CEO / CFO / CMO perspective', 'Not academic → decision-oriented'] },
                        { title: ' 4. Placement & Career Advantage', details: ['AI-powered portfolio', 'Industry-ready mindset'] },
                        { title: ' 5. Scalable Model (Higher Ed Focus)', details: ['Add-on certification', 'Credit-based program', 'Placement accelerator'] }
                      ],
                      approach: [
                        '✅ Build 2–3 real business use cases using AI',
                        '✅ Create: Reports, Dashboards & Presentations',
                        '✅ Work on live case studies',
                        '✅ Present in AI Business Pitch Day'
                      ],
                      outcomes: [
                        '✔ Use AI tools confidently',
                        '✔ Apply AI in business scenarios',
                        '✔ Build professional outputs',
                        '✔ Improve employability significantly',
                        '✔ Develop AI-first mindset'
                      ],
                      positioning: '“AI Pragati by The DataTech Labs transforms non-technical graduates into AI-enabled business professionals — ready to lead, decide, and innovate in an AI-driven economy.”'
                    })}
                  >
                    View Details
                  </button>
                </div>
              </div>

              <div className="kpi-card">
                <div className="kpi-logo-container">
                  <img src={shilpLogo} alt="AI Shilp Logo" className="kpi-logo" />
                </div>
                <h3 className="gradient-text-accent kpi-title">AI Shilp</h3>
                <p className="kpi-desc">Advanced AI for engineering & technical students</p>

                <ul className="kpi-list">
                  <li> Python, ML & GenAI coding</li>
                  <li> Agentic AI & modern systems</li>
                  <li> Portfolio GitHub projects</li>
                </ul>

                <div className="view-details-btn-wrapper">
                  <button
                    className="view-details-btn"
                    onClick={() => openModal({
                      id: 'shilp',
                      icon: shilpLogo,
                      title: 'AI Shilp',
                      subtitle: 'Engineering Intelligence Program (EIIP)',
                      desc: 'Advanced, implementation-driven AI program for all engineering & diploma branches. Mentored by Dr. Amit Andre.',
                      modules: [
                        {
                          title: 'Module 1: AI/ML Foundations + System Thinking (4 Hours)',
                          details: ['AI, ML, DL architecture overview', 'Model lifecycle (Data → Training → Deployment)', 'Introduction to LLMs & Agentic AI systems'],
                          outcome: 'Outcome: Understand full-stack AI system design'
                        },
                        {
                          title: 'Module 2: Python for AI & Data Handling (6 Hours)',
                          details: ['Python essentials (focused for AI)', 'Libraries: NumPy, Pandas', 'Data preprocessing techniques'],
                          outcome: 'Hands-on: Load, Clean & Transform Data'
                        },
                        {
                          title: 'Module 3: Machine Learning Essentials (8 Hours)',
                          details: ['Supervised: Regression & Classification', 'Model evaluation (accuracy, precision, recall)', 'Intro to Scikit-learn'],
                          outcome: 'Hands-on: Build performance predictors & recommendation models'
                        },
                        {
                          title: 'Module 4: Deep Learning & Computer Vision (8 Hours)',
                          details: ['Neural Networks (The basics)', 'CNNs and Image Recognition', 'Object detection simplified'],
                          outcome: 'Hands-on: Build an AI that recognizes objects/faces'
                        },
                        {
                          title: 'Module 5: GenAI & LLM Implementation (8 Hours)',
                          details: ['Introduction to LLMs', 'Using OpenAI API / LangChain', 'Building small GenAI applications'],
                          outcome: 'Hands-on: Build a specialized AI Chatbot for a technical use case'
                        },
                        {
                          title: 'Module 6: Capstone Project – Industry Implementation (6 Hours)',
                          details: ['Final project execution (Branch specific)', 'Deployment on cloud (Basic intro)', 'AI Code documentation & Pitching'],
                          outcome: 'Final Demo Day + Portfolio Showcase'
                        }
                      ],
                      usps: [
                        { title: ' 1. Deep Implementation – Not just Theory', details: ['Focus on code and deployment', 'Industry-standard workflows'] },
                        { title: ' 2. AI + Branch Specialization', details: ['Students build AI tools for: Mechanical, Civil, E&TC, CS/IT', '👉 Contextual AI implementation'] },
                        { title: ' 3. Portfolio & GitHub Focus', details: ['Ready portfolio for tech interviews', 'Open-source contribution awareness'] },
                        { title: ' 4. Exposure to Agentic AI', details: ['Learn about AI agents, not just simple chat', 'Stay ahead with the latest enterprise trends'] },
                        { title: ' 5. Industry-Expert Led', details: ['Dr. Amit Andre & TDTL specialized engineers', 'Mentorship for tech leadership'] },
                        { title: ' 6. Internship Opportunities', details: ['Top performers integrated into TDTL ecosystem', 'Live project exposure'] },
                        { title: ' 7. Global Standards Certification', details: ['Recognized by TDTL & industry partners', 'Increases global employability'] }
                      ],
                      whyUs: [
                        { title: ' 1. We are an AI Engineering Company', details: ['Daily work involves: Large Scale LLM deployments, Agentic frameworks', '👉 Students learn what engineers actually do'] },
                        { title: ' 2. Real Architecture Exposure', details: ['Cloud integration, API management', 'Scalable AI systems design thinking'] },
                        { title: ' 3. End-to-End Tech Mentor Support', details: ['Technical query resolution', 'Project guidance'] },
                        { title: ' 4. Focus on “Future of Work”', details: ['Preparing students for jobs that don’t exist yet'] },
                        { title: ' 5. Sustainable Tech Ecosystem', details: ['Community access to AI forums and events'] }
                      ],
                      approach: [
                        '✅ Build 3+ GitHub-ready AI projects',
                        '✅ Code in Python using real libraries',
                        '✅ Deploy models on test environments',
                        '✅ Participate in Hackathons / Demo Day'
                      ],
                      outcomes: [
                        '✔ Code and implement AI models',
                        '✔ Build branch-specific AI applications',
                        '✔ Showcase technical portfolio',
                        '✔ Get industry-ready for AI-driven engineering roles',
                        '✔ Develop problem-solving mindset using AI'
                      ],
                      positioning: '“AI Shilp by The DataTech Labs bridges the gap between traditional engineering and the AI future — molding students into creators of intelligence, not just users of tools.”'
                    })}
                  >
                    View Details
                  </button>
                </div>
              </div>

            </div>

            {/* Program Detail Modal */}
            {isModalOpen && activeProgram && (
              <div className="modal-overlay active" onClick={closeModal} style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(8px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{
                  background: 'rgba(10, 31, 68, 0.9)',
                  backdropFilter: 'blur(20px)',
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: '85vh',
                  borderRadius: '24px',
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  boxShadow: '0 0 50px rgba(0, 229, 255, 0.2)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'modalScaleIn 0.3s ease-out'
                }}>
                  <button className="modal-close-btn" onClick={closeModal} style={{
                    position: 'absolute',
                    top: '20px',
                    right: '25px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    fontSize: '20px',
                    cursor: 'pointer',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>×</button>

                  <div className="modal-content" style={{ padding: '50px', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
                      <img src={activeProgram.icon} alt={activeProgram.title} style={{
                        height: '120px',
                        width: '120px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        boxShadow: '0 0 40px rgba(0, 229, 255, 0.3)',
                        background: 'transparent'
                      }} />
                      <div>
                        <h2 style={{ fontSize: '2.5rem', fontWeight: '900', margin: 0, color: '#fff' }}>
                          <span className="gradient-text-accent">{activeProgram.title}</span>
                        </h2>
                        <p style={{ color: 'var(--accent-cyan)', fontSize: '1.1rem', margin: '5px 0 0', fontWeight: '600' }}>{activeProgram.subtitle}</p>
                      </div>
                    </div>

                    <p style={{ color: 'var(--text-gray)', fontSize: '1.2rem', marginBottom: '35px', borderLeft: '4px solid var(--accent-cyan)', paddingLeft: '20px' }}>
                      {activeProgram.desc}
                    </p>

                    <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '20px', padding: '25px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '30px' }}>
                      <h4 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        📋 40-Hour Curriculum (Hover to Expand)
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {activeProgram.modules ? activeProgram.modules.map((module, idx) => (
                          <div key={idx} style={{
                            border: '1px solid rgba(0,229,255,0.1)',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: expandedModule === idx ? 'rgba(0,229,255,0.05)' : 'transparent',
                            transition: 'all 0.3s ease'
                          }}>
                            <div
                              onMouseEnter={() => setExpandedModule(idx)}
                              style={{
                                padding: '15px 20px',
                                cursor: 'default',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontWeight: '700',
                                color: expandedModule === idx ? 'var(--accent-cyan)' : '#fff'
                              }}
                            >
                              <span>{module.title}</span>
                            </div>

                            {expandedModule === idx && (
                              <div style={{ padding: '0 20px 20px', color: '#CBD5E0', fontSize: '0.9rem', animation: 'fadeIn 0.3s ease' }}>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0' }}>
                                  {module.details.map((detail, dIdx) => (
                                    <li key={dIdx} style={{ marginBottom: '8px', display: 'flex', gap: '8px' }}>
                                      <span style={{ color: 'var(--accent-cyan)' }}>•</span> {detail}
                                    </li>
                                  ))}
                                </ul>
                                {module.outcome && (
                                  <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(0,229,255,0.1)', borderRadius: '8px', borderLeft: '4px solid var(--accent-cyan)' }}>
                                    <strong style={{ color: '#fff' }}>👉 {module.outcome}</strong>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )) : (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {activeProgram.bullets.map((point, idx) => (
                              <li key={idx} style={{ color: '#CBD5E0', fontSize: '0.95rem', marginBottom: '12px', display: 'flex', gap: '8px', lineHeight: '1.5' }}>
                                <span style={{ color: 'var(--accent-cyan)' }}>🔹</span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                      <div style={{ padding: '20px', background: 'rgba(0,229,255,0.03)', borderRadius: '15px', border: '1px solid rgba(0,229,255,0.1)' }}>
                        <h5 style={{ color: 'var(--accent-cyan)', marginBottom: '15px' }}>🚀 Hands-on Approach</h5>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {activeProgram.approach ? activeProgram.approach.map((a, i) => (
                            <li key={i} style={{ color: '#fff', fontSize: '0.85rem', marginBottom: '8px' }}>{a}</li>
                          )) : (
                            <li style={{ color: '#fff', fontSize: '0.85rem' }}>Core differentiator logic...</li>
                          )}
                        </ul>
                      </div>
                      <div style={{ padding: '20px', background: 'rgba(0,229,255,0.03)', borderRadius: '15px', border: '1px solid rgba(0,229,255,0.1)' }}>
                        <h5 style={{ color: 'var(--accent-cyan)', marginBottom: '10px' }}>🎯 Program Outcomes</h5>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {Array.isArray(activeProgram.outcomes) ? activeProgram.outcomes.map((o, i) => (
                            <li key={i} style={{ color: '#fff', fontSize: '0.85rem', marginBottom: '6px' }}>{o}</li>
                          )) : (
                            <li style={{ color: '#fff', fontSize: '0.9rem', lineHeight: '1.5' }}>{activeProgram.outcomes}</li>
                          )}
                        </ul>
                      </div>
                    </div>

                    {activeProgram.usps && typeof activeProgram.usps[0] === 'object' && (
                      <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '20px', padding: '25px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '30px' }}>
                        <h4 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          🌟 Program USPs (Hover to Expand)
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {activeProgram.usps.map((usp, idx) => (
                            <div key={idx} style={{
                              border: '1px solid rgba(255,200,0,0.1)',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              background: expandedModule === `usp-${idx}` ? 'rgba(255,200,0,0.05)' : 'transparent'
                            }}>
                              <div
                                onMouseEnter={() => setExpandedModule(`usp-${idx}`)}
                                style={{ padding: '12px 15px', cursor: 'default', display: 'flex', justifyContent: 'space-between', fontWeight: '600', color: '#fff' }}
                              >
                                <span style={{ fontSize: '0.95rem' }}>{usp.title}</span>
                              </div>
                              {expandedModule === `usp-${idx}` && (
                                <div style={{ padding: '0 15px 15px', color: '#CBD5E0', fontSize: '0.85rem' }}>
                                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {usp.details.map((d, di) => <li key={di} style={{ marginBottom: '4px' }}>• {d}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activeProgram.whyUs && (
                      <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '20px', padding: '25px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '30px' }}>
                        <h4 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          Why AI Gurukul❓
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {activeProgram.whyUs.map((item, idx) => (
                            <div key={idx} style={{
                              border: '1px solid rgba(0,229,255,0.1)',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              background: expandedModule === `why-${idx}` ? 'rgba(0,229,255,0.05)' : 'transparent'
                            }}>
                              <div
                                onMouseEnter={() => setExpandedModule(`why-${idx}`)}
                                style={{ padding: '12px 15px', cursor: 'default', display: 'flex', justifyContent: 'space-between', fontWeight: '600', color: '#fff' }}
                              >
                                <span style={{ fontSize: '0.95rem' }}>{item.title}</span>
                              </div>
                              {expandedModule === `why-${idx}` && (
                                <div style={{ padding: '0 15px 15px', color: '#CBD5E0', fontSize: '0.85rem' }}>
                                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {item.details.map((d, di) => <li key={di} style={{ marginBottom: '4px' }}>• {d}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p style={{ textAlign: 'center', color: 'var(--text-gray)', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: '20px', padding: '0 40px', lineHeight: '1.6' }}>
                      {activeProgram.positioning || "“Powered by The DataTech Labs — Making India the AI Capital of the World”"}
                    </p>


                    <div style={{ textAlign: 'center' }}>
                      <button className="btn-primary" onClick={() => { closeModal(); scrollToSection(sections.register); }} style={{ padding: '12px 40px', fontSize: '1.1rem' }}>
                        Secure Your Spot
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Differentiators */}
            <div className="glass-card differentiators-card">
              <h2 className="section-title diff-title">What Makes Our AI Programs Different?</h2>
              <div className="diff-grid">
                <div className="diff-item">✅ Zero cost model – no financial barrier</div>
                <div className="diff-item">✅ 70% hands-on learning, 30% theory</div>
                <div className="diff-item">✅ Build real AI projects, not just concepts</div>
                <div className="diff-item">✅ Designed by The Data Tech Labs (AI company)</div>
                <div className="diff-item">✅ Based on real industry use cases</div>
                <div className="diff-item">✅ Separate programs for school, non-tech & eng.</div>
                <div className="diff-item">✅ Covers latest GenAI & Agentic AI</div>
                <div className="diff-item">✅ Focus on logic, creativity & problem-solving</div>
                <div className="diff-item">✅ Includes Demo Day / project presentation</div>
                <div className="diff-item">✅ Builds confidence & communication skills</div>
                <div className="diff-item">✅ Industry-recognized certification</div>
                <div className="diff-item">✅ Helps in career readiness & placements</div>
                <div className="diff-item">✅ Encourages startup thinking</div>
                <div className="diff-item">✅ Supports schools in building AI clubs</div>
              </div>
              <p style={{ textAlign: 'center', marginTop: '40px', fontSize: '1.5rem', fontWeight: '800', color: 'var(--accent-cyan)' }}>
                Not just a course — a movement to spread AI awareness 🚀
              </p>
            </div>
          </div>
        </section>

        <section id={sections.urgency} className="urgency-section fade-in-section">
          <div className="section-container">
            <h2 className="urgency-title">
              Limited Seats. Personalized Learning.
            </h2>
            <p className="urgency-subtitle">
              Don’t wait until it’s too late. Start early. Stay ahead.
            </p>
            <button className="btn-primary" onClick={() => scrollToSection(sections.register)} style={{ padding: '20px 60px', fontSize: '1.3rem' }}>
              🔵 Enroll Now
            </button>
          </div>
        </section>

        <section id={sections.register} className="final-cta-section" style={{
          backgroundImage: `url(${bg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(5, 14, 33, 0.9)', zIndex: 0 }}></div>
          <div className="section-container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="cta-grid">
              <div className="cta-content">
                <h2 className="cta-content-title">
                  Start Your Child’s <span className="gradient-text-accent">AI Journey</span> Today
                </h2>
                <p style={{ color: 'var(--text-gray)', fontSize: '1.2rem', marginBottom: '40px' }}>
                  Invest in the education that matters. Prepare for Tomorrow, Today. Secure your slot in the upcoming batch.
                </p>
                <div className="hero-buttons">
                  <button className="btn-primary" style={{ padding: '15px 40px' }}>Enroll Now</button>
                  <a href={`https://wa.me/${whatsappNumber}?text=Hi%2C%20I%20have%20a%20question%20about%20AI%20Gurukul.`} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ padding: '15px 40px', textDecoration: 'none', display: 'inline-block' }}>
                    Talk to Advisor
                  </a>
                </div>
              </div>
              <div className="glass-card" style={{ padding: '40px', background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', borderRadius: '32px', border: '1px solid rgba(0, 229, 255, 0.2)' }}>
                <RegistrationForm />
              </div>
            </div>
          </div>
        </section>

      </div> {/* End of Flex:1 content wrapper */}

      {/* FOOTER */}
      <footer className="footer-section" style={{
        background: 'linear-gradient(135deg, #020617, #0A1F44)',
        padding: '30px 0 30px',
        borderTop: '1px solid rgba(0,229,255,0.2)',
        marginTop: 'auto'
      }}>
        <div className="section-container">
          <div className="footer-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px',
            alignItems: 'start'
          }}>
            {/* Column 1: Brand */}
            <div className="footer-brand">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <img src={logo} alt="AI Gurukul Logo" style={{
                  height: '180px',
                  width: '180px',
                  borderRadius: '50%',
                  objectFit: 'cover'

                }} />
              </div>
              <p style={{ color: '#94A3B8', fontSize: '0.9rem', lineHeight: '1.6', marginTop: '0' }}>
                Empowering the next generation with cutting-edge AI skills. Journey from curiosity to creation.
              </p>
            </div>

            {/* Column 2: Links */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '25px' }}>Quick Links</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li className="footer-link">About Us</li>
                <li className="footer-link">Programs</li>
                <li className="footer-link">Contact</li>
                <li className="footer-link">Privacy Policy</li>
              </ul>
            </div>

            {/* Column 3: Programs */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '25px' }}>Programs</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li className="footer-link">AI Udaan (Std 7-12)</li>
                <li className="footer-link">AI Pragati (UG/PG)</li>
                <li className="footer-link">AI Shilp (Engineering)</li>
              </ul>
            </div>

            {/* Column 4: Social */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '25px' }}>Connect</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li className="footer-link">LinkedIn</li>
                <li className="footer-link">Twitter</li>
                <li className="footer-link">Instagram</li>
              </ul>
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: '60px',
            paddingTop: '30px',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            color: '#94A3B8',
            fontSize: '0.9rem'
          }}>
            © 2026 The Data Tech Labs. All rights reserved.
          </div>
        </div>
      </footer>

      {/* EXIT INTENT POPUP */}
      {showExitPopup && (
        <div className="exit-popup-overlay" onClick={() => setShowExitPopup(false)}>
          <div className="glass-card exit-popup" onClick={e => e.stopPropagation()}>
            <button className="close-popup" onClick={() => setShowExitPopup(false)} style={{ border: 'none', background: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            <span className="popup-badge" style={{ background: 'var(--accent-cyan)', color: 'var(--bg-darker)', padding: '4px 12px', borderRadius: '12px', fontWeight: '800', fontSize: '0.8rem', marginBottom: '15px', display: 'inline-block' }}>WAIT! DON'T MISS OUT</span>
            <h2>Get a Free <span className="gradient-text-accent">AI Roadmap</span></h2>
            <p>Join 1000+ parents who have already started their child's AI journey.</p>
            <button className="btn-primary" onClick={() => { setShowExitPopup(false); scrollToSection(sections.register); }}>
              🚀 Enroll Now
            </button>
          </div>
        </div>
      )}
      <WhatsAppBot isOpen={isBotOpen} setIsOpen={setIsBotOpen} />
    </div>
  );
}